/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

const DECIMAL_TYPE = {
  TRUNCATE: 'TRUNCATE',
} as const;

type DecimalType = (typeof DECIMAL_TYPE)[keyof typeof DECIMAL_TYPE];

type FormatPriceAmountParams = {
  amount?: string | number | null;
  locale?: string;
  decimalType?: DecimalType;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
};

const currencyFormat = (
  amount?: string | number | null,
  decimalType?: DecimalType,
): string => {
  const numericValue = Number(amount);

  if (!Number.isFinite(numericValue)) return '';

  switch (decimalType) {
    case DECIMAL_TYPE.TRUNCATE:
      return String(Math.trunc(numericValue));
    default:
      return String(numericValue);
  }
};

export const formatPriceAmount = ({
  amount,
  locale = 'en-gb',
  decimalType,
  maximumFractionDigits = 0,
  minimumFractionDigits = 0,
}: FormatPriceAmountParams): string => {
  const formattedAmount = currencyFormat(amount, decimalType);

  if (!formattedAmount) return '';
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits,
    minimumFractionDigits,
  }).format(Number(formattedAmount));
};

export const formatHotelPriceLabel = (
  currency?: string,
  value?: string | number | null,
  locale: string = 'en-gb',
): string => {
  if (value === undefined || value === null || value === '') return '';

  const currencyCode = currency?.trim().toUpperCase() ?? '';
  const formattedValue = formatPriceAmount({
    amount: value,
    decimalType: DECIMAL_TYPE.TRUNCATE,
    locale,
  });

  return formattedValue ? `${currencyCode} ${formattedValue}`.trim() : '';
};

const isBlankCurrency = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === '';

export const resolveHotelSearchCurrency = (
  candidates: Array<string | undefined | null>,
  fallback: string,
): string => {
  for (const candidate of candidates) {
    if (!isBlankCurrency(candidate)) {
      return String(candidate).trim().toUpperCase();
    }
  }

  return fallback.trim().toUpperCase();
};
